<template>
	<v-card v-if="offsets.length" variant="outlined" class="mt-4 mb-2">
		<v-card-subtitle class="pb-0">
			相对配方 #{{ recipe.id }} 的参数偏移
			<span class="text-caption text-medium-emphasis ml-1">
				雷达中点=标称、两端=配方上下限（按上下偏移百分比归一化）；悬停或点击最近数据点查看详情
			</span>
		</v-card-subtitle>
		<v-card-text>
			<v-chart
				ref="radarChart"
				v-if="chartVisible"
				:option="chartOption"
				:init-options="{ renderer: 'canvas' }"
				autoresize
				style="height: 340px; width: 100%"
				@click="onChartPointer"
				@mousemove="onChartPointer"
			/>
			<v-alert
				v-if="selectedOffset"
				type="info"
				variant="tonal"
				density="compact"
				class="mt-2 mb-2"
				closable
				@click:close="selectedKey = null"
			>
				<strong class="mr-2">{{ selectedOffset.label }}</strong>
				<span class="mr-3">标称 {{ selectedOffset.nominalText }}</span>
				<span class="mr-3">实际 {{ selectedOffset.actualText }}</span>
				<span class="mr-3">偏移 {{ selectedOffset.deltaAbsText }}</span>
				<span class="mr-3">相对标称 {{ selectedOffset.deltaPct || '—' }}</span>
				<span>相对范围 {{ selectedOffset.rangePct || '—' }}</span>
			</v-alert>
			<v-table density="compact" class="mt-2 recipe-offset-table">
				<thead>
					<tr>
						<th>参数</th>
						<th>本次</th>
						<th>配方</th>
						<th>偏移</th>
						<th>配方范围</th>
						<th>状态</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="row in offsets"
						:key="row.key"
						:class="{ 'recipe-offset-row--active': selectedKey === row.key }"
						@click="selectedKey = row.key"
					>
						<td>{{ row.label }}</td>
						<td>{{ row.actualText }}</td>
						<td>{{ row.nominalText }}</td>
						<td :class="row.inRange ? '' : 'text-warning'">
							{{ row.deltaAbsText }}
							<span v-if="row.deltaPct" class="text-medium-emphasis">（{{ row.deltaPct }}）</span>
						</td>
						<td class="text-caption">{{ row.rangeText }}</td>
						<td>
							<v-chip size="x-small" :color="row.inRange ? 'success' : 'warning'" variant="tonal">
								{{ row.inRange ? '范围内' : '越界' }}
							</v-chip>
						</td>
					</tr>
				</tbody>
			</v-table>
		</v-card-text>
	</v-card>
</template>

<script>
import { RadarChart } from 'echarts/charts'
import { LegendComponent, TooltipComponent } from 'echarts/components'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import VChart from 'vue-echarts'
import { computeRecipeOffsets } from './recipeOffset.js'

use([CanvasRenderer, RadarChart, TooltipComponent, LegendComponent])

export default {
	name: 'RecipeOffsetRadar',
	components: { VChart },
	props: {
		recipe: { type: Object, default: null },
		roast: { type: Object, default: null },
		profile: { type: Object, default: null },
		/** 弹窗内使用时设为 true 后再渲染，避免宽高为 0 */
		active: { type: Boolean, default: true }
	},
	data() {
		return {
			chartVisible: false,
			selectedKey: null,
			hoverKey: null
		}
	},
	watch: {
		active: {
			handler(on) {
				if (!on) {
					this.chartVisible = false
					this.selectedKey = null
					this.hoverKey = null
					return
				}
				this.$nextTick(() => {
					requestAnimationFrame(() => {
						this.chartVisible = true
					})
				})
			},
			immediate: true
		},
		offsets() {
			if (this.selectedKey && !this.offsets.some((o) => o.key === this.selectedKey)) {
				this.selectedKey = null
			}
			if (this.hoverKey && !this.offsets.some((o) => o.key === this.hoverKey)) {
				this.hoverKey = null
			}
		}
	},
	computed: {
		offsetData() {
			if (!this.recipe) return { offsets: [], indicators: [], recipeSeries: [], roastSeries: [] }
			return computeRecipeOffsets(this.recipe, this.roast, this.profile)
		},
		offsets() {
			return this.offsetData.offsets
		},
		selectedOffset() {
			if (!this.selectedKey) return null
			return this.offsets.find((o) => o.key === this.selectedKey) || null
		},
		chartOption() {
			const { indicators, recipeSeries, roastSeries } = this.offsetData
			if (!indicators.length) return {}
			return {
				tooltip: {
					trigger: 'item',
					triggerOn: 'mousemove|click',
					confine: true,
					enterable: false,
					formatter: (params) => {
						const row = this.nearestOffsetFromParams(params)
						return row ? this.formatOffsetTip(row) : ''
					}
				},
				legend: {
					data: ['配方标称', '本次烘焙'],
					bottom: 0
				},
				radar: {
					indicator: indicators,
					center: ['50%', '48%'],
					radius: '58%',
					splitNumber: 2,
					axisName: { fontSize: 11 },
					splitArea: { show: true }
				},
				series: [
					{
						type: 'radar',
						symbol: 'circle',
						symbolSize: 12,
						data: [
							{
								name: '配方标称',
								value: recipeSeries,
								lineStyle: { type: 'dashed', color: '#1976d2' },
								itemStyle: { color: '#1976d2' },
								areaStyle: { opacity: 0.05 }
							},
							{
								name: '本次烘焙',
								value: roastSeries,
								lineStyle: { color: '#ed6c02' },
								itemStyle: { color: '#ed6c02' },
								areaStyle: { opacity: 0.12, color: '#ed6c02' }
							}
						]
					}
				]
			}
		}
	},
	methods: {
		formatOffsetTip(row) {
			return [
				`<strong>${row.label}</strong>`,
				`标称: ${row.nominalText}`,
				`实际: ${row.actualText}`,
				`偏移: ${row.deltaAbsText}`,
				`相对标称: ${row.deltaPct || '—'}`,
				`相对范围: ${row.rangePct || '—'}（0%=标称，±100%=上下限）`
			].join('<br/>')
		},
		getChart() {
			const ref = this.$refs.radarChart
			if (!ref) return null
			return typeof ref.getEchartsInstance === 'function' ? ref.getEchartsInstance() : ref.chart || null
		},
		nearestOffsetFromParams(params) {
			const idx = this.nearestDimIndexFromParams(params)
			return idx >= 0 ? this.offsets[idx] : null
		},
		nearestDimIndexFromParams(params) {
			if (!params) return -1

			// 1) ECharts 点到符号上时直接带维度下标
			const fromTarget = params.event?.target?.__dimIdx
			if (typeof fromTarget === 'number' && fromTarget >= 0) return fromTarget
			const fromParent = params.event?.target?.parent?.__dimIdx
			if (typeof fromParent === 'number' && fromParent >= 0) return fromParent
			if (typeof params.dimensionIndex === 'number' && params.dimensionIndex >= 0) {
				return params.dimensionIndex
			}

			// 2) 用图表坐标系算与各顶点的欧氏距离（最近数据点）
			const byDist = this.nearestDimByPointDistance(params)
			if (byDist >= 0) return byDist

			if (params.name) {
				const byLabel = this.offsets.findIndex((o) => o.label === params.name)
				if (byLabel >= 0) return byLabel
			}
			return -1
		},
		onChartPointer(params) {
			const idx = this.nearestDimIndexFromParams(params)
			if (idx < 0 || !this.offsets[idx]) return
			const key = this.offsets[idx].key
			this.hoverKey = key
			const nativeType = params?.event?.event?.type || params?.event?.type
			if (nativeType === 'click' || nativeType === 'touchend') {
				this.selectedKey = key
			}
		},
		/** 取指针在 ECharts/ZRender 坐标系中的位置 */
		pointerInChart(params) {
			const ev = params?.event
			if (!ev) return null
			if (typeof ev.zrX === 'number' && typeof ev.zrY === 'number') {
				return { x: ev.zrX, y: ev.zrY }
			}
			const native = ev.event || ev
			const chart = this.getChart()
			if (!chart || !native) return null
			const el = chart.getDom()
			if (!el) return null
			const rect = el.getBoundingClientRect()
			const clientX = native.clientX ?? native.touches?.[0]?.clientX
			const clientY = native.clientY ?? native.touches?.[0]?.clientY
			if (clientX == null || clientY == null) return null
			// 转换为与 getWidth/getHeight 一致的逻辑像素
			const x = ((clientX - rect.left) / rect.width) * chart.getWidth()
			const y = ((clientY - rect.top) / rect.height) * chart.getHeight()
			return { x, y }
		},
		/** 读取雷达实际中心与半径（与绘制一致） */
		getRadarLayout() {
			const chart = this.getChart()
			if (!chart) return null
			try {
				const seriesList = chart.getModel().getSeries()
				for (let i = 0; i < seriesList.length; i++) {
					const s = seriesList[i]
					const coord = s.coordinateSystem
					if (coord && typeof coord.cx === 'number' && typeof coord.r === 'number') {
						return { cx: coord.cx, cy: coord.cy, r: coord.r }
					}
				}
			} catch (_) {
				/* ignore */
			}
			// fallback：按 option 百分比估算
			const w = chart.getWidth()
			const h = chart.getHeight()
			return { cx: w * 0.5, cy: h * 0.48, r: Math.min(w, h) * 0.58 * 0.5 }
		},
		/**
		 * 计算第 i 个指标在雷达上的像素坐标。
		 * ECharts：第 0 轴在正上方，之后顺时针。
		 */
		radarVertexPixel(layout, index, value0to100) {
			const n = this.offsets.length
			const ang = (index * 2 * Math.PI) / n
			const ratio = Math.max(0, Math.min(100, Number(value0to100) || 0)) / 100
			const rr = layout.r * ratio
			return {
				x: layout.cx + rr * Math.sin(ang),
				y: layout.cy - rr * Math.cos(ang)
			}
		},
		nearestDimByPointDistance(params) {
			const n = this.offsets.length
			if (n <= 0) return -1
			const pointer = this.pointerInChart(params)
			const layout = this.getRadarLayout()
			if (!pointer || !layout || !(layout.r > 0)) return -1

			const { recipeSeries, roastSeries } = this.offsetData
			let bestIdx = -1
			let bestDist = Infinity

			const consider = (idx, value) => {
				const p = this.radarVertexPixel(layout, idx, value)
				const d = (p.x - pointer.x) ** 2 + (p.y - pointer.y) ** 2
				if (d < bestDist) {
					bestDist = d
					bestIdx = idx
				}
			}

			for (let i = 0; i < n; i++) {
				// 本次烘焙顶点、配方标称顶点
				consider(i, roastSeries[i])
				consider(i, recipeSeries[i])
				// 轴外端，方便点到轴标签附近时仍命中该属性
				consider(i, 100)
			}

			return bestIdx
		}
	}
}
</script>

<style scoped>
.recipe-offset-table :deep(th),
.recipe-offset-table :deep(td) {
	font-size: 0.8rem;
}
.recipe-offset-table tbody tr {
	cursor: pointer;
}
.recipe-offset-row--active {
	background: rgba(237, 108, 2, 0.08);
}
</style>
